/**
 * Staff Mode (team lead) — dedicated mobile-first view.
 *
 * Why a separate route from /login:
 *   - Team leads use a fundamentally different credential (venue_code + PIN, no email).
 *   - The post-login UI is also fundamentally different (no nav, no analytics,
 *     no settings) — they get a streamlined moderation + queue surface and
 *     nothing else.
 *   - Keeps the host LoginPage simple and uncluttered.
 *
 * Flow:
 *   1. If `/auth/me` returns role=team_lead → land on the staff dashboard.
 *   2. Otherwise show the venue_code + PIN form.
 *   3. After login, fetch active session, pending requests, pending shoutouts.
 *      Poll every 5s.
 *   4. Tabbed interface: Requests | Shoutouts | Add Song.
 *      Each row has BIG approve / reject buttons (mobile-thumb-friendly).
 *      Add Song tab does Apple Music search + injects via /queue/inject.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ShieldCheck, Loader2, Check, X, Music, Search, Play, Pause, SkipForward,
  Volume2, LogOut, MessageSquare, Inbox, Plus, Bell, Sliders,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============== Login sub-component ==============
const StaffLogin = ({ onLoggedIn, prefilledVenueCode = '' }) => {
  const [venueCode, setVenueCode] = useState(prefilledVenueCode);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  // PWA install-prompt hook — Chrome/Edge/Android fire `beforeinstallprompt`
  // when the site is installable. We capture it here so we can show a
  // one-tap "Install app" button; staff pin the DJ Hand-off view to their
  // home screen and it launches standalone (no browser chrome).
  const [installEvt, setInstallEvt] = useState(null);
  const codePrefilled = !!prefilledVenueCode;

  useEffect(() => {
    if (prefilledVenueCode) setVenueCode(prefilledVenueCode);
  }, [prefilledVenueCode]);

  useEffect(() => {
    const onBIP = (e) => {
      e.preventDefault();
      setInstallEvt(e);
    };
    window.addEventListener('beforeinstallprompt', onBIP);
    return () => window.removeEventListener('beforeinstallprompt', onBIP);
  }, []);

  // Manifest swap for /dj-handoff/* now happens in index.html BEFORE React
  // mounts — the browser needs the scoped manifest at `beforeinstallprompt`
  // time, which fires before any React useEffect runs. See public/index.html.

  const install = async () => {
    if (!installEvt) return;
    try {
      installEvt.prompt();
      await installEvt.userChoice;
    } catch { /* dismissed */ }
    setInstallEvt(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/auth/team-lead/login`, {
        venue_code: venueCode.trim().toUpperCase(),
        pin: pin.trim(),
      });
      toast.success('Welcome');
      onLoggedIn();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-5">
      <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="staff-login-form">
        <div className="text-center">
          <ShieldCheck className="w-12 h-12 mx-auto text-[#FF007F] mb-3" />
          <h1 className="text-2xl font-bold">DJ Hand-off</h1>
          <p className="text-sm text-white/50 mt-1">
            {codePrefilled
              ? `Signing in to ${prefilledVenueCode} — enter the 6-digit PIN your DJ shared.`
              : 'Scan the QR from your DJ, or enter your venue code + 6-digit PIN.'}
          </p>
        </div>
        <div className="space-y-3">
          {!codePrefilled && (
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-white/50 font-semibold">Venue code</span>
              <input
                data-testid="venue-code-input"
                type="text"
                autoComplete="off"
                autoCapitalize="characters"
                value={venueCode}
                onChange={(e) => setVenueCode(e.target.value)}
                placeholder="HOPS-XXXXXX"
                className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#FF007F] font-mono tracking-widest uppercase"
                required
              />
            </label>
          )}
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-white/50 font-semibold">PIN</span>
            <input
              data-testid="pin-input"
              type="tel"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              pattern="[0-9]{6}"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="••••••"
              autoFocus={codePrefilled}
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#FF007F] text-center font-mono text-2xl tracking-[0.4em]"
              required
            />
          </label>
        </div>
        <button
          data-testid="staff-login-submit"
          type="submit"
          disabled={busy || venueCode.length < 4 || pin.length !== 6}
          className="w-full bg-[#FF007F] hover:bg-[#FF007F]/90 text-white font-bold py-3 rounded-xl flex items-center justify-center disabled:opacity-40"
        >
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Log in'}
        </button>

        {/* PWA install nudge — only visible before login and only when the
            browser has actually fired beforeinstallprompt (Chrome / Edge /
            most Android). iOS Safari doesn't fire it — we show a subtle
            "Add to Home Screen" hint instead. */}
        {installEvt ? (
          <button
            type="button"
            onClick={install}
            data-testid="dj-handoff-install"
            className="w-full mt-1 border border-white/15 hover:border-white/30 rounded-xl py-2.5 text-xs uppercase tracking-widest text-white/70 hover:text-white transition-colors"
          >
            Install app on this device
          </button>
        ) : (
          <p className="text-[10px] text-white/30 text-center mt-1 font-mono">
            Tip · add this page to your Home Screen for a full-screen app.
          </p>
        )}
      </form>
    </div>
  );
};

// ============== Dashboard sub-component ==============
const StaffDashboard = ({ identity, onLogout }) => {
  const [tab, setTab] = useState('requests');  // 'requests' | 'shoutouts' | 'add'
  const [session, setSession] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [pendingShoutouts, setPendingShoutouts] = useState([]);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  // Playback control state — we mirror what the DJ Console publishes via
  // PUT /playback/state, and DRIVE remote commands via POST /playback/remote.
  // Kept optimistic so the slider/buttons feel snappy while the DJ Console
  // executes the actual MusicKit calls on the next 1s poll.
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(100);
  const [txBusy, setTxBusy] = useState(false); // "transport" busy — locks buttons briefly
  const pollRef = useRef(null);
  // Notification primitives — fired when pending counts grow.
  //   - prevCountsRef: last-seen counts so we can detect growth across polls.
  //   - chimeRef: lazily-built short tone using WebAudio (no asset needed).
  //   - notifiedAtMountRef: skip the first tick so the chime doesn't fire on
  //     initial page load even when there are already-pending items.
  const prevCountsRef = useRef({ requests: 0, shoutouts: 0 });
  const notifiedAtMountRef = useRef(false);
  const chimeRef = useRef(null);
  const playChime = useCallback(() => {
    try {
      // Lazy-init the AudioContext on first call — required by autoplay policy
      // (must follow a user gesture; team-lead login click counts).
      if (!chimeRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        chimeRef.current = new Ctx();
      }
      const ctx = chimeRef.current;
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const t0 = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      // Two-note pleasant chime: A5 → E6
      osc.frequency.setValueAtTime(880, t0);
      osc.frequency.setValueAtTime(1318, t0 + 0.09);
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.25, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.22);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.24);
    } catch {
      /* AudioContext blocked / iframed / quota — fail silently */
    }
  }, []);
  const buzz = useCallback(() => {
    try { navigator.vibrate?.([60, 40, 60]); } catch { /* unsupported */ }
  }, []);

  // === Web Push subscribe button ===
  // OS-level notifications even when the /staff tab is closed. Three states:
  //   'unsupported' — browser doesn't support Push (no button shown)
  //   'subscribed'  — we have a valid PushSubscription on the backend
  //   'idle'        — supported but not subscribed yet (show "Enable" button)
  const [pushState, setPushState] = useState('idle');
  const isPushSupported = typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;

  useEffect(() => {
    if (!isPushSupported) { setPushState('unsupported'); return; }
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const existing = await reg.pushManager.getSubscription();
        setPushState(existing ? 'subscribed' : 'idle');
      } catch { /* noop */ }
    })();
  }, [isPushSupported]);

  const urlBase64ToUint8Array = (b64) => {
    const padding = '='.repeat((4 - (b64.length % 4)) % 4);
    const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
  };

  const enablePush = async () => {
    if (!isPushSupported) return;
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { toast.error('Notification permission denied'); return; }
      const reg = await navigator.serviceWorker.ready;
      const vapidB64 = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      if (!vapidB64) { toast.error('Push not configured'); return; }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidB64),
      });
      await axios.post(`${API}/push/subscribe`, { subscription: sub.toJSON() });
      setPushState('subscribed');
      toast.success('Notifications enabled. Your phone will buzz on new requests.');
    } catch (err) {
      console.warn('[StaffPage] push subscribe failed:', err);
      toast.error('Could not enable notifications');
    }
  };

  const refresh = useCallback(async () => {
    try {
      const sRes = await axios.get(`${API}/requests/sessions/active`);
      const sess = sRes.data;
      setSession(sess);
      if (sess?.id) {
        const [reqRes, shoRes, qRes, stRes] = await Promise.all([
          axios.get(`${API}/requests/sessions/${sess.id}/requests`),
          axios.get(`${API}/requests/sessions/${sess.id}/shoutouts`),
          axios.get(`${API}/queue/current`).catch(() => ({ data: null })),
          axios.get(`${API}/playback/state`).catch(() => ({ data: null })),
        ]);
        const nextReq = (reqRes.data || []).filter((r) => r.status === 'pending');
        const nextSho = (shoRes.data || []).filter((s) => s.status === 'pending');
        // Fire chime + haptic ONLY when counts have grown vs the previous
        // tick, and only after the first poll has settled (so we don't ping
        // the user the moment they log in to a backlog of pending items).
        const prev = prevCountsRef.current;
        if (notifiedAtMountRef.current && (nextReq.length > prev.requests || nextSho.length > prev.shoutouts)) {
          playChime();
          buzz();
        }
        prevCountsRef.current = { requests: nextReq.length, shoutouts: nextSho.length };
        notifiedAtMountRef.current = true;
        setPendingRequests(nextReq);
        setPendingShoutouts(nextSho);
        setNowPlaying(qRes.data?.current_track || null);
        // Sync transport + volume from the DJ Console's authoritative state
        // (published every 3s). Don't stomp on an in-flight `txBusy` slider drag.
        if (stRes.data && !txBusy) {
          setIsPlaying(!!stRes.data.is_playing);
          if (typeof stRes.data.volume === 'number') setVolume(stRes.data.volume);
        }
      } else {
        setPendingRequests([]);
        setPendingShoutouts([]);
      }
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
    }
  }, [onLogout, playChime, buzz, txBusy]);

  // ── Remote transport / volume — writes to /playback/remote which the DJ
  //    Console drains every 1s. We flash `txBusy` briefly so quick taps don't
  //    fight the incoming state poll.
  const sendRemote = useCallback(async (command, payload = {}) => {
    setTxBusy(true);
    try {
      await axios.post(`${API}/playback/remote`, { command, payload });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Command failed');
    } finally {
      // Release after 900ms — longer than the DJ Console's 1s poll interval
      // is overkill; 900ms feels responsive without immediate stomp-back.
      setTimeout(() => setTxBusy(false), 900);
    }
  }, []);

  const togglePlay = () => {
    const next = !isPlaying;
    setIsPlaying(next);
    sendRemote(next ? 'play' : 'pause');
  };
  const skipNext = () => sendRemote('next');
  const changeVolume = (v) => {
    const clamped = Math.max(0, Math.min(100, Number(v) || 0));
    setVolume(clamped);
    sendRemote('set_volume', { volume: clamped });
  };

  useEffect(() => {
    refresh();
    pollRef.current = setInterval(refresh, 5000);
    return () => clearInterval(pollRef.current);
  }, [refresh]);

  const approveRequest = async (id) => {
    try { await axios.put(`${API}/requests/${id}/approve`); refresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Approve failed'); }
  };
  const rejectRequest = async (id) => {
    try { await axios.put(`${API}/requests/${id}/reject`); refresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Reject failed'); }
  };
  const approveShoutout = async (id) => {
    try { await axios.put(`${API}/shoutouts/${id}/approve`); refresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Approve failed'); }
  };
  const rejectShoutout = async (id) => {
    try { await axios.put(`${API}/shoutouts/${id}/reject`); refresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Reject failed'); }
  };

  const runSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const r = await axios.get(`${API}/music/search`, { params: { q, limit: 12 } });
      // /api/music/search returns { tracks: [...] } — grabbing r.data caused
      // .map to blow up when the object landed in state instead of the array.
      setSearchResults(r.data?.tracks || []);
    } catch (err) {
      toast.error('Search failed');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // debounced search
  useEffect(() => {
    const id = setTimeout(() => runSearch(searchQ), 350);
    return () => clearTimeout(id);
  }, [searchQ, runSearch]);

  const injectTrack = async (track) => {
    try {
      await axios.post(`${API}/queue/inject`, { track });
      toast.success(`Queued: ${track.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add');
    }
  };

  const doLogout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch { /* noop */ }
    onLogout();
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" style={{ paddingBottom: 'calc(120px + env(safe-area-inset-bottom))' }}>
      {/* Sticky header — venue name + logout */}
      <header className="sticky top-0 z-20 bg-black/90 backdrop-blur-md border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-white/40">DJ Hand-off</p>
          <h1 className="text-lg font-bold leading-tight" data-testid="staff-venue-name">
            {identity?.venue_name || identity?.name || 'Venue'}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {pushState === 'idle' && (
            <button
              onClick={enablePush}
              data-testid="enable-push-btn"
              className="bg-[#FF007F]/15 hover:bg-[#FF007F]/25 text-[#FF007F] border border-[#FF007F]/40 rounded-lg px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5"
              title="Get push notifications even when this tab is closed"
            >
              <Bell className="w-3.5 h-3.5" />Enable alerts
            </button>
          )}
          {pushState === 'subscribed' && (
            <div
              data-testid="push-enabled-indicator"
              className="text-[#39FF14]/80 flex items-center gap-1 text-xs"
              title="Push notifications are on"
            >
              <Bell className="w-3.5 h-3.5" />On
            </div>
          )}
          <button
            onClick={doLogout}
            data-testid="staff-logout-btn"
            className="text-white/60 hover:text-white p-2"
            aria-label="Log out"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Tab bar — sticky just under the header so it's always in reach.
          Docked player + transport moved to the bottom of the screen. */}
      <nav className="sticky top-[60px] z-10 grid grid-cols-3 gap-1 px-4 pt-3 pb-2 bg-black/90 backdrop-blur-md border-b border-white/5">
        {[
          { k: 'requests',  label: 'Requests',  icon: Inbox,         count: pendingRequests.length },
          { k: 'shoutouts', label: 'Shoutouts', icon: MessageSquare, count: pendingShoutouts.length },
          { k: 'add',       label: 'Add Song',  icon: Plus,          count: 0 },
        ].map(({ k, label, icon: Icon, count }) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`tab-${k}`}
            className={`flex flex-col items-center gap-1 py-2 rounded-lg font-medium text-xs transition ${
              tab === k ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white/80'
            }`}
          >
            <div className="relative">
              <Icon className="w-5 h-5" />
              {count > 0 && (
                <span className="absolute -top-1 -right-2 bg-[#FF007F] text-white text-[10px] font-bold rounded-full px-1.5 min-w-[16px] text-center leading-4">
                  {count}
                </span>
              )}
            </div>
            {label}
          </button>
        ))}
      </nav>

      {/* Tab content */}
      <main className="flex-1 px-4 mt-3 space-y-3">
        {tab === 'requests' && (
          pendingRequests.length === 0 ? (
            <EmptyState icon={Inbox} message="No pending requests" />
          ) : pendingRequests.map((r) => (
            <ApprovalCard
              key={r.id}
              testId={`req-${r.id}`}
              title={r.track?.name}
              subtitle={(r.track?.artists || []).join(', ')}
              meta={`from ${r.guest_name || 'guest'}`}
              image={r.track?.album_image}
              onApprove={() => approveRequest(r.id)}
              onReject={() => rejectRequest(r.id)}
            />
          ))
        )}

        {tab === 'shoutouts' && (
          pendingShoutouts.length === 0 ? (
            <EmptyState icon={MessageSquare} message="No pending shoutouts" />
          ) : pendingShoutouts.map((s) => (
            <ApprovalCard
              key={s.id}
              testId={`shout-${s.id}`}
              title={s.guest_name || 'Anonymous'}
              subtitle={s.message}
              image={s.photo_data_url}
              isShoutout
              onApprove={() => approveShoutout(s.id)}
              onReject={() => rejectShoutout(s.id)}
            />
          ))
        )}

        {tab === 'add' && (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                data-testid="staff-search-input"
                type="text"
                placeholder="Search Apple Music..."
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-3 py-3 text-sm focus:outline-none focus:border-[#FF007F]"
              />
              {searching && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 animate-spin" />
              )}
            </div>
            {searchQ.length >= 2 && searchResults.length === 0 && !searching && (
              <EmptyState icon={Music} message="No results" />
            )}
            {searchResults.map((t) => (
              <div
                key={t.id}
                data-testid={`search-result-${t.id}`}
                className="bg-white/[0.04] border border-white/5 rounded-xl p-3 flex items-center gap-3"
              >
                {t.album_image ? (
                  <img src={t.album_image} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded bg-white/5 flex items-center justify-center flex-shrink-0">
                    <Music className="w-5 h-5 text-white/30" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{t.name}</p>
                  <p className="text-xs text-white/50 truncate">{(t.artists || []).join(', ')}</p>
                </div>
                <button
                  onClick={() => injectTrack(t)}
                  data-testid={`add-track-${t.id}`}
                  className="bg-[#39FF14]/20 hover:bg-[#39FF14]/30 text-[#39FF14] rounded-lg p-2"
                  aria-label="Add to queue"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </main>

      {!session && (
        <div className="mt-10 mx-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 text-sm text-yellow-200">
          <strong>No active live session.</strong> The venue host needs to start a session
          from their Settings page before you can approve requests or add tracks.
        </div>
      )}

      {/* Docked player + transport — fixed to the bottom of the screen so
          the tabs above (Requests / Shoutouts / Add) never fight for space
          with the always-visible controls. safe-area-inset covers iPhone
          home-indicator + PWA notches. */}
      <div
        className="fixed inset-x-0 z-30 bg-gradient-to-r from-[#1a0016] to-[#001a1a] border-t border-white/10 px-4 pt-2 pb-2 space-y-1.5"
        style={{ bottom: 0, paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}
        data-testid="staff-transport-strip"
      >
        <div className="flex items-center gap-3">
          <Volume2 className="w-4 h-4 text-[#FF007F] flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-white/50 uppercase tracking-wider">Now playing</p>
            {nowPlaying ? (
              <p className="text-sm font-medium truncate">
                {nowPlaying.name} <span className="text-white/50">— {(nowPlaying.artists || []).join(', ')}</span>
              </p>
            ) : (
              <p className="text-sm text-white/40">— No track</p>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={togglePlay}
              data-testid="tx-playpause"
              disabled={txBusy}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center disabled:opacity-40"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
            </button>
            <button
              onClick={skipNext}
              data-testid="tx-skip"
              disabled={txBusy}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center disabled:opacity-40"
              aria-label="Skip"
            >
              <SkipForward className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Sliders className="w-3.5 h-3.5 text-white/40 flex-shrink-0" />
          <input
            type="range"
            min={0} max={100}
            value={volume}
            onChange={(e) => changeVolume(e.target.value)}
            data-testid="tx-volume"
            className="flex-1 accent-[#FF007F] h-1.5 cursor-pointer"
            aria-label="Volume"
          />
          <span className="text-[10px] font-mono text-white/50 w-8 text-right" data-testid="tx-volume-value">{volume}%</span>
        </div>
      </div>
    </div>
  );
};

// ============== Helper components ==============
const ApprovalCard = ({ testId, title, subtitle, meta, image, isShoutout, onApprove, onReject }) => (
  <div
    data-testid={testId}
    className="bg-white/[0.04] border border-white/10 rounded-2xl p-4"
  >
    <div className="flex items-start gap-3 mb-3">
      {image ? (
        <img
          src={image}
          alt=""
          className={`flex-shrink-0 ${isShoutout ? 'w-16 h-16 rounded-xl' : 'w-12 h-12 rounded'} object-cover`}
        />
      ) : (
        <div className="w-12 h-12 rounded bg-white/5 flex items-center justify-center flex-shrink-0">
          {isShoutout
            ? <MessageSquare className="w-5 h-5 text-white/30" />
            : <Music className="w-5 h-5 text-white/30" />}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="font-semibold truncate">{title || '—'}</p>
        {subtitle && (
          <p className={`text-sm text-white/60 ${isShoutout ? '' : 'truncate'}`}>{subtitle}</p>
        )}
        {meta && <p className="text-xs text-white/40 mt-1">{meta}</p>}
      </div>
    </div>
    <div className="grid grid-cols-2 gap-2">
      <button
        onClick={onReject}
        data-testid={`${testId}-reject`}
        className="bg-white/[0.04] hover:bg-red-500/20 active:bg-red-500/30 border border-white/10 hover:border-red-500/40 text-red-300 font-bold py-3 rounded-xl flex items-center justify-center"
      >
        <X className="w-5 h-5 mr-1" /> Reject
      </button>
      <button
        onClick={onApprove}
        data-testid={`${testId}-approve`}
        className="bg-[#39FF14]/15 hover:bg-[#39FF14]/30 active:bg-[#39FF14]/40 border border-[#39FF14]/30 text-[#39FF14] font-bold py-3 rounded-xl flex items-center justify-center"
      >
        <Check className="w-5 h-5 mr-1" /> Approve
      </button>
    </div>
  </div>
);

const EmptyState = ({ icon: Icon, message }) => (
  <div className="bg-white/[0.02] border border-white/5 rounded-2xl py-10 px-4 text-center">
    <Icon className="w-8 h-8 mx-auto text-white/20 mb-3" />
    <p className="text-sm text-white/40">{message}</p>
  </div>
);

// ============== Top-level page ==============
const StaffPage = () => {
  const [loading, setLoading] = useState(true);
  const [identity, setIdentity] = useState(null);  // null until /auth/me resolves; {} once a team_lead is in
  const navigate = useNavigate();
  // URL-based venue prefill for the QR-scan flow: `/dj-handoff/:venueCode`.
  const { venueCode: urlVenueCode } = useParams();

  const probe = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/me`);
      if (r.data?.role === 'team_lead') {
        // Backend still tags the display name with " — Team Lead" for backward
        // compat; strip either legacy or new suffix so the venue name is clean.
        const rawName = r.data.name || '';
        setIdentity({
          name: rawName,
          venue_name: rawName.replace(/\s—\s(Team Lead|DJ Hand-?off)$/i, ''),
        });
      } else {
        // Host or superadmin landed here by mistake — send them to their normal dashboard.
        navigate('/dashboard', { replace: true });
      }
    } catch {
      setIdentity(null);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { probe(); }, [probe]);

  const onLogout = () => { setIdentity(null); };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-white/40 animate-spin" />
      </div>
    );
  }

  if (!identity) {
    return <StaffLogin onLoggedIn={probe} prefilledVenueCode={urlVenueCode || ''} />;
  }

  return <StaffDashboard identity={identity} onLogout={onLogout} />;
};

export default StaffPage;
